import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { IconType } from "react-icons";
import { FaFacebookF, FaRedditAlien, FaSms, FaTwitter, FaWhatsapp } from "react-icons/fa";
import { FaBluesky } from "react-icons/fa6";
import { neighborhoods } from "./data";
import { MobileRatingTray } from "./MobileRatingTray";
import { PhillyMap } from "./PhillyMap";
import { buildProfile } from "./profile";
import { buildSocialShareLinks } from "./share";
import { clearLocalRatings, loadLocalRatings, sanitizeRatings, saveLocalRatings } from "./storage";
import { TIERS, TIER_BY_VALUE } from "./tiers";
import type { PhillyProfile, Rating, Ratings, SharedMap } from "./types";

const orderedNeighborhoods = [...neighborhoods.features].sort((a, b) => a.properties.name.localeCompare(b.properties.name));
type FocusableElement = Element & { focus: () => void };

export default function App() {
  const startsOnSharedRoute = /^\/m\/[A-Za-z0-9_-]{12}$/.test(window.location.pathname);
  const [ratings, setRatings] = useState<Ratings>(() => loadLocalRatings());
  const [selectedId, setSelectedId] = useState<string | null>(() => firstUnrated(loadLocalRatings()));
  const [selectMany, setSelectMany] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [resultOpen, setResultOpen] = useState(false);
  const [sharedTitle, setSharedTitle] = useState("");
  const [sharedId, setSharedId] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareStatus, setShareStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [shareNotice, setShareNotice] = useState("");
  const [loadingShared, setLoadingShared] = useState(startsOnSharedRoute);
  const [sharedError, setSharedError] = useState<string | null>(null);
  const [viewingShared, setViewingShared] = useState(startsOnSharedRoute);
  const [announcement, setAnnouncement] = useState("");
  const [mobileTrayOpen, setMobileTrayOpen] = useState(false);
  const [mobileTrayFocusOnOpen, setMobileTrayFocusOnOpen] = useState(false);
  const [mobileTrayFocusRequest, setMobileTrayFocusRequest] = useState(0);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const mobileTrayReturnFocusRef = useRef<FocusableElement | null>(null);
  const skipNextPersistence = useRef(false);
  const saveSequence = useRef(0);
  const interactionLocked = loadingShared || viewingShared;

  const selected = selectedId
    ? neighborhoods.features.find((feature) => String(feature.properties.id) === selectedId) ?? null
    : null;
  const activeIds = selectMany ? selectedIds : selectedId ? [selectedId] : [];
  const activeRatings = activeIds.map((id) => ratings[id]).filter((rating): rating is Rating => rating !== undefined);
  const commonRating = activeIds.length && activeRatings.length === activeIds.length && activeRatings.every((rating) => rating === activeRatings[0])
    ? activeRatings[0]
    : undefined;
  const ratedCount = Object.keys(ratings).length;
  const profile = useMemo(() => buildProfile(ratings, neighborhoods), [ratings]);
  const counts = useMemo(
    () => Object.fromEntries(TIERS.map((tier) => [tier.value, Object.values(ratings).filter((rating) => rating === tier.value).length])) as Record<Rating, number>,
    [ratings],
  );
  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (!needle) return [];
    return orderedNeighborhoods.filter((feature) => feature.properties.name.toLocaleLowerCase().includes(needle)).slice(0, 8);
  }, [query]);

  useEffect(() => {
    if (skipNextPersistence.current) {
      skipNextPersistence.current = false;
      return;
    }
    saveLocalRatings(ratings);
  }, [ratings]);

  useEffect(() => {
    const match = window.location.pathname.match(/^\/m\/([A-Za-z0-9_-]{12})$/);
    if (!match) return;
    saveSequence.current += 1;
    const controller = new AbortController();
    void fetch(`/api/maps/${match[1]}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("Shared map unavailable");
        const payload: unknown = await response.json();
        if (!isSharedMap(payload)) throw new Error("Shared map response is invalid");
        return payload;
      })
      .then((snapshot) => {
        const safeRatings = sanitizeRatings(snapshot.ratings);
        skipNextPersistence.current = true;
        setRatings(safeRatings);
        setSharedTitle(snapshot.title);
        setSharedId(snapshot.id);
        setShareUrl(window.location.href);
        setViewingShared(true);
        setSelectMany(false);
        setSelectedIds([]);
        setSelectedId(firstUnrated(safeRatings));
        setAnnouncement("Shared map loaded.");
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        window.history.replaceState({}, "", "/");
        setViewingShared(false);
        setSharedError("That shared map is unavailable. Your own local draft is shown below and was not changed.");
        setAnnouncement("That shared map could not be loaded. Your local draft is unchanged.");
      })
      .finally(() => setLoadingShared(false));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!resultOpen) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    function handleDialogKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setResultOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const dialog = document.querySelector<HTMLElement>(".profile-dialog");
      if (!dialog) return;
      const focusable = [...dialog.querySelectorAll<HTMLElement>("button:not([disabled]), a[href], input:not([disabled])")];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleDialogKey);
    return () => {
      document.removeEventListener("keydown", handleDialogKey);
      document.body.style.overflow = originalOverflow;
      previousFocus?.focus();
    };
  }, [resultOpen]);

  function openMobileTray(focusOnOpen = false) {
    mobileTrayReturnFocusRef.current = focusOnOpen && isFocusableElement(document.activeElement) ? document.activeElement : null;
    setMobileTrayFocusOnOpen(focusOnOpen);
    if (focusOnOpen) setMobileTrayFocusRequest((current) => current + 1);
    setMobileTrayOpen(true);
  }

  function closeMobileTray(restoreFocus = true) {
    const returnFocus = restoreFocus ? mobileTrayReturnFocusRef.current : null;
    mobileTrayReturnFocusRef.current = null;
    setMobileTrayFocusOnOpen(false);
    setMobileTrayOpen(false);
    if (returnFocus) restoreFocusAfterRender(returnFocus);
  }

  function selectNeighborhood(id: string, source: "pointer" | "keyboard" = "pointer") {
    const wasSelected = selectedIds.includes(id);
    const keyboardOrigin = source === "keyboard" && isFocusableElement(document.activeElement) ? document.activeElement : null;
    if (selectMany) {
      setSelectedIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
    } else {
      setSelectedIds([]);
    }
    setSelectedId(id);
    if (!interactionLocked) {
      const removesLastSelection = selectMany && wasSelected && selectedIds.length === 1;
      if (removesLastSelection) {
        closeMobileTray(false);
        if (keyboardOrigin) restoreFocusAfterRender(keyboardOrigin);
      }
      else openMobileTray(source === "keyboard");
    }
    const feature = neighborhoods.features.find((item) => String(item.properties.id) === id);
    if (feature) setAnnouncement(selectMany && wasSelected ? `${feature.properties.name} removed from selection.` : `${feature.properties.name} selected.`);
  }

  function paintNeighborhood(id: string, action: "add" | "remove") {
    if (!selectMany || interactionLocked) return;
    setSelectedId(id);
    setSelectedIds((current) => {
      if (action === "add") return current.includes(id) ? current : [...current, id];
      return current.filter((item) => item !== id);
    });
  }

  function setMapMode(multiple: boolean) {
    if (multiple && interactionLocked) return;
    setSelectMany(multiple);
    closeMobileTray(false);
    if (multiple) {
      setSelectedIds((current) => current.length ? current : selectedId ? [selectedId] : []);
    }
    setAnnouncement(multiple
      ? `Select several mode on. ${selectedIds.length || (selectedId ? 1 : 0)} neighborhoods selected.`
      : `Move map mode on. ${selectedIds.length ? `${selectedIds.length} selected neighborhoods kept.` : "Drag to pan the map."}`);
  }

  function clearSelection() {
    setSelectedIds([]);
    closeMobileTray(false);
  }

  const rateSelected = useCallback((rating: Rating, advance = true) => {
    const idsToRate = selectMany ? selectedIds : selectedId ? [selectedId] : [];
    if (!idsToRate.length || interactionLocked) return;
    setRatings((current) => {
      const next = { ...current };
      idsToRate.forEach((id) => { next[id] = rating; });
      return next;
    });
    saveSequence.current += 1;
    setSharedId(null);
    setShareUrl(null);
    setShareStatus("idle");
    setShareNotice("");
    leaveSharedRoute();
    setAnnouncement(selectMany
      ? `${idsToRate.length} neighborhoods rated ${TIER_BY_VALUE[rating].label}.`
      : `${selected?.properties.name ?? "Neighborhood"}: ${TIER_BY_VALUE[rating].label}.`);
    if (advance && !selectMany && selectedId) {
      const next = nextUnrated(selectedId, { ...ratings, [selectedId]: rating });
      if (next) setSelectedId(next);
    }
  }, [interactionLocked, ratings, selectMany, selected?.properties.name, selectedId, selectedIds]);

  useEffect(() => {
    function handleRatingShortcut(event: KeyboardEvent) {
      if (event.repeat || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || resultOpen || interactionLocked || !activeIds.length) return;
      if (isEditableTarget(event.target)) return;
      const shortcutIndex = Number(event.key) - 1;
      const tier = TIERS[shortcutIndex];
      if (!tier) return;
      event.preventDefault();
      rateSelected(tier.value);
    }

    document.addEventListener("keydown", handleRatingShortcut);
    return () => document.removeEventListener("keydown", handleRatingShortcut);
  }, [activeIds.length, interactionLocked, rateSelected, resultOpen]);

  function resetMap() {
    if (!window.confirm("Clear every rating and start over?")) return;
    clearLocalRatings();
    setRatings({});
    saveSequence.current += 1;
    setSelectedId(String(orderedNeighborhoods[0].properties.id));
    setSelectMany(false);
    setSelectedIds([]);
    closeMobileTray(false);
    setSharedId(null);
    setShareUrl(null);
    setSharedTitle("");
    setShareNotice("");
    setResultOpen(false);
    setViewingShared(false);
    window.history.replaceState({}, "", "/");
    setAnnouncement("Map cleared.");
  }

  async function saveAndShare() {
    if (!ratedCount || shareStatus === "saving" || loadingShared) return;
    if (sharedId && shareUrl) {
      try {
        await copyText(shareUrl);
        setShareStatus("saved");
        setShareNotice("Share link copied.");
        setAnnouncement("Share link copied to clipboard.");
      } catch {
        setShareStatus("saved");
        setShareNotice("The map is saved. Copy the share link below manually.");
        setAnnouncement("The map is saved. Copy the visible link manually.");
      }
      return;
    }
    const sequence = saveSequence.current + 1;
    saveSequence.current = sequence;
    const ratingsAtSave = ratings;
    const titleAtSave = sharedTitle;
    setShareStatus("saving");
    setShareNotice("");
    try {
      const response = await fetch("/api/maps", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ratings: ratingsAtSave, title: titleAtSave }),
      });
      if (!response.ok) throw new Error("Could not save map");
      const payload: unknown = await response.json();
      if (!isSaveResponse(payload)) throw new Error("Save response is invalid");
      if (saveSequence.current !== sequence) return;
      const data = payload;
      const url = new URL(data.path, window.location.origin).toString();
      setSharedId(data.id);
      setShareUrl(url);
      setShareStatus("saved");
      window.history.replaceState({}, "", data.path);
      try {
        await copyText(url);
        setShareNotice("Share link copied.");
        setAnnouncement("Share link copied to clipboard.");
      } catch {
        setShareNotice("The map is saved. Copy the share link below manually.");
        setAnnouncement("The map is saved. Copy the visible link manually.");
      }
    } catch {
      if (saveSequence.current !== sequence) return;
      setShareStatus("error");
      setShareNotice("");
      setAnnouncement("The map could not be saved. Please try again.");
    }
  }

  function remixSharedMap() {
    saveLocalRatings(ratings);
    saveSequence.current += 1;
    setViewingShared(false);
    setSelectMany(false);
    setSelectedIds([]);
    setSharedId(null);
    setShareUrl(null);
    setShareStatus("idle");
    setShareNotice("");
    leaveSharedRoute();
    setAnnouncement("Shared map copied into your local draft. You can now edit it.");
  }

  function returnToLocalDraft() {
    const localRatings = loadLocalRatings();
    skipNextPersistence.current = true;
    setRatings(localRatings);
    setSelectedId(firstUnrated(localRatings));
    setViewingShared(false);
    setSelectMany(false);
    setSelectedIds([]);
    setSharedId(null);
    setShareUrl(null);
    setSharedTitle("");
    setShareStatus("idle");
    setShareNotice("");
    setResultOpen(false);
    leaveSharedRoute();
    setAnnouncement("Your local draft has been restored.");
  }

  return (
    <div className="site-shell" aria-busy={loadingShared}>
      <a className="skip-link" href="#rating-controls">Skip to rating controls</a>
      <div className="top-stripe" aria-hidden="true" />
      <header className="masthead">
        <a className="wordmark" href="/" aria-label="My Country, Philadelphia home">
          <span className="seal" aria-hidden="true"><span>PHL</span><small>1682</small></span>
          <span><strong>My Country,</strong><em>Philadelphia</em></span>
        </a>
        <div className="masthead-note">
          <span>One very personal map</span>
          <span>159 neighborhoods</span>
        </div>
      </header>

      <main>
        <section className="hero-copy" aria-labelledby="page-title">
          <p className="kicker"><span>★</span> Where would you live? <span>★</span></p>
          <h1 id="page-title">Make your map.<br /><i>Find your Philly.</i></h1>
          <p className="dek">Rate the neighborhoods you know, see what kind of Philadelphian your map makes you, and share it with friends.</p>
        </section>

        {sharedError ? <div className="shared-error-banner" role="alert"><strong>Shared map not found.</strong><span>{sharedError}</span></div> : null}

        <section className="map-workbench" aria-label="Philadelphia neighborhood rater">
          <div className="map-column">
            <div className="map-placard">
              <div className="placard-heading">
                <span>{viewingShared && sharedTitle ? sharedTitle : "Philadelphia neighborhood map"}</span>
                <strong>{ratedCount} / 159 rated</strong>
              </div>
              <PhillyMap
                neighborhoods={neighborhoods}
                ratings={ratings}
                selectedId={selectedId}
                selectedIds={selectMany ? selectedIds : selectedId ? [selectedId] : []}
                selectMany={selectMany}
                interactionDisabled={interactionLocked}
                onSelect={selectNeighborhood}
                onNavigate={setSelectedId}
                onPaint={paintNeighborhood}
                onSelectionComplete={() => openMobileTray(false)}
                onModeChange={setMapMode}
                onClearSelection={clearSelection}
              />
              <div className="map-compass" aria-hidden="true"><b>N</b><span>✦</span></div>
              <div className="legend" aria-label="Map legend">
                {TIERS.map((tier) => (
                  <span key={tier.value}><i data-rating={tier.value} />{tier.shortLabel} <b>{counts[tier.value]}</b></span>
                ))}
                <span><i data-rating="unrated" />Unrated <b>{159 - ratedCount}</b></span>
              </div>
            </div>
            <p className="data-note">Boundaries: OpenDataPhilly / Robert Cheetham · CC BY 4.0</p>
          </div>

          <aside className="rating-panel" id="rating-controls">
            {interactionLocked ? (
              <div className="shared-view-notice">
                <strong>{loadingShared ? "Opening shared map" : "Shared map"}</strong>
                <span>{loadingShared ? "Loading this map. Your own saved map is untouched." : "You can look around without changing your own saved map."}</span>
                {!loadingShared ? <button type="button" onClick={remixSharedMap}>Make my own copy</button> : null}
              </div>
            ) : null}
            <div className="progress-block">
              <div className="progress-copy"><span>Map progress</span><strong>{Math.round((ratedCount / 159) * 100)}%</strong></div>
              <div className="progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={159} aria-valuenow={ratedCount} aria-label={`${ratedCount} of 159 neighborhoods rated`}>
                <span style={{ width: `${(ratedCount / 159) * 100}%` }} />
              </div>
            </div>

            <div className="search-block">
              <label htmlFor="neighborhood-search">Find a neighborhood</label>
              <input
                id="neighborhood-search"
                type="search"
                value={query}
                disabled={loadingShared}
                placeholder="Search all 159…"
                autoComplete="off"
                onChange={(event) => setQuery(event.target.value)}
              />
              {filtered.length ? (
                <div className="search-results">
                  {filtered.map((feature) => {
                    const id = String(feature.properties.id);
                    const rating = ratings[id];
                    return (
                      <button key={id} type="button" onClick={(event) => { selectNeighborhood(id, event.detail === 0 ? "keyboard" : "pointer"); setQuery(""); }}>
                        <span>{feature.properties.name}</span>
                        <small>{rating === undefined ? "Unrated" : TIER_BY_VALUE[rating].label}</small>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            <div className="ballot">
              <p className="ballot-overline">{selectMany ? "Rate several at once" : "Selected neighborhood"}</p>
              <h2>{selectMany ? `${selectedIds.length} selected` : selected?.properties.name ?? "Select a neighborhood"}</h2>
              <p>{selectMany ? "Click or drag over neighborhoods, then choose one answer for all of them." : "Would you live here?"}</p>
              <p className="shortcut-hint">Keyboard: <kbd>1</kbd> Absolutely through <kbd>5</kbd> Never</p>
              <div className="tier-buttons" role="radiogroup" aria-label={selectMany ? `Rate ${selectedIds.length} selected neighborhoods` : `Would you live in ${selected?.properties.name ?? "this neighborhood"}?`}>
                {TIERS.map((tier, index) => {
                  const checked = commonRating === tier.value;
                  const hasCheckedTier = commonRating !== undefined;
                  return (
                  <button
                    key={tier.value}
                    type="button"
                    className="tier-button"
                    role="radio"
                    aria-checked={checked}
                    aria-keyshortcuts={String(index + 1)}
                    tabIndex={checked || (!hasCheckedTier && index === 0) ? 0 : -1}
                    data-rating={tier.value}
                    data-active={checked ? "true" : "false"}
                    disabled={!activeIds.length || interactionLocked}
                    aria-disabled={!activeIds.length || interactionLocked}
                    onClick={() => rateSelected(tier.value)}
                    onKeyDown={(event) => {
                      const keys = ["ArrowLeft", "ArrowUp", "ArrowRight", "ArrowDown", "Home", "End"];
                      if (!keys.includes(event.key)) return;
                      event.preventDefault();
                      const buttons = [...event.currentTarget.parentElement!.querySelectorAll<HTMLButtonElement>('[role="radio"]')];
                      const direction = event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 1;
                      const nextIndex = event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1 : (index + direction + buttons.length) % buttons.length;
                      rateSelected(TIERS[nextIndex].value, false);
                      requestAnimationFrame(() => buttons[nextIndex]?.focus());
                    }}
                  >
                    <i aria-hidden="true" />
                    <span><strong>{tier.label}</strong><small>{tier.detail}</small></span>
                    <kbd aria-hidden="true">{index + 1}</kbd>
                    <b>{counts[tier.value]}</b>
                  </button>
                  );
                })}
              </div>
              {selectMany ? (
                <button className="next-button" type="button" onClick={clearSelection} disabled={!selectedIds.length}>Clear selection <span aria-hidden="true">×</span></button>
              ) : (
                <button className="next-button" type="button" onClick={() => setSelectedId(nextUnrated(selectedId, ratings))} disabled={ratedCount === 159 || loadingShared}>
                  Skip to next unrated <span aria-hidden="true">→</span>
                </button>
              )}
            </div>

            <div className="panel-actions">
              <button className="result-button" type="button" onClick={() => setResultOpen(true)} disabled={!ratedCount || loadingShared}>
                See my Philly type <span aria-hidden="true">✦</span>
              </button>
              <button className="text-button" type="button" onClick={viewingShared ? returnToLocalDraft : resetMap} disabled={loadingShared}>
                {viewingShared ? "Return to my map" : "Clear the map"}
              </button>
            </div>
          </aside>
        </section>
      </main>

      {mobileTrayOpen && activeIds.length && !interactionLocked ? (
        <MobileRatingTray
          commonRating={commonRating}
          focusOnOpen={mobileTrayFocusOnOpen}
          focusRequest={mobileTrayFocusRequest}
          label={selectMany ? `${selectedIds.length} selected` : selected?.properties.name ?? "Selected neighborhood"}
          onDismiss={closeMobileTray}
          onRate={(rating) => {
            rateSelected(rating, false);
            closeMobileTray();
          }}
        />
      ) : null}

      <footer>
        <strong>There are 159 neighborhoods. You can have opinions about all of them.</strong>
        <span>Made in the spirit of Philly. No neighborhood slander intended.</span>
      </footer>

      <div className="sr-only" aria-live="polite">{announcement}</div>

      {resultOpen ? (
        <ProfileDialog
          profile={profile}
          ratedCount={ratedCount}
          counts={counts}
          title={sharedTitle}
          shareUrl={shareUrl}
          shareStatus={shareStatus}
          shareNotice={shareNotice}
          sharedId={sharedId}
          viewingShared={viewingShared}
          loadingShared={loadingShared}
          closeButtonRef={closeButtonRef}
          onTitleChange={(value) => {
            if (loadingShared) return;
            saveSequence.current += 1;
            leaveSharedRoute();
            setSharedTitle(value);
            setSharedId(null);
            setShareUrl(null);
            setShareStatus("idle");
            setShareNotice("");
          }}
          onClose={() => setResultOpen(false)}
          onShare={() => void saveAndShare()}
        />
      ) : null}
    </div>
  );
}

interface ProfileDialogProps {
  profile: PhillyProfile;
  ratedCount: number;
  counts: Record<Rating, number>;
  title: string;
  shareUrl: string | null;
  shareStatus: "idle" | "saving" | "saved" | "error";
  shareNotice: string;
  sharedId: string | null;
  viewingShared: boolean;
  loadingShared: boolean;
  closeButtonRef: React.RefObject<HTMLButtonElement | null>;
  onTitleChange: (value: string) => void;
  onClose: () => void;
  onShare: () => void;
}

function ProfileDialog({ profile, ratedCount, counts, title, shareUrl, shareStatus, shareNotice, sharedId, viewingShared, loadingShared, closeButtonRef, onTitleChange, onClose, onShare }: ProfileDialogProps) {
  const shareTitle = title.trim();
  const socialShareText = shareTitle
    ? `${shareTitle}: ${profile.name} — My Country, Philadelphia`
    : `${profile.name} — My Country, Philadelphia`;
  const socialLinks = shareUrl ? buildSocialShareLinks(shareUrl, socialShareText) : null;

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="profile-dialog" role="dialog" aria-modal="true" aria-labelledby="profile-title">
        <button ref={closeButtonRef} className="dialog-close" type="button" onClick={onClose} aria-label="Close profile">×</button>
        <div className="certificate-rule"><span>✦</span><b>Your Philly type</b><span>✦</span></div>
        {title ? <p className="shared-map-title">Map of “{title}”</p> : null}
        <p className="profile-kicker">{profile.eyebrow} · {profile.confidence}</p>
        <h2 id="profile-title">{profile.name}</h2>
        <p className="profile-description">{profile.description}</p>
        <p className="profile-reason">Why this result: {profile.reason}</p>

        <div className="profile-badges">
          {profile.badges.map((badge) => (
            <div key={badge.label}><strong>{badge.label}</strong><span>{badge.detail}</span></div>
          ))}
        </div>

        <div className="mini-tally" aria-label="Rating tally">
          {TIERS.map((tier) => <span key={tier.value} data-rating={tier.value}><i />{counts[tier.value]}</span>)}
          <b>{ratedCount} of 159 rated</b>
        </div>

        <div className="share-box">
          <label htmlFor="map-title">Name this map <small>optional</small></label>
          <input id="map-title" value={title} maxLength={80} disabled={shareStatus === "saving" || viewingShared || loadingShared} placeholder="e.g. Jacob’s Philly" onChange={(event) => onTitleChange(event.target.value)} />
          <button className="share-button" type="button" onClick={onShare} disabled={shareStatus === "saving" || !ratedCount || loadingShared}>
            {shareStatus === "saving" ? "Saving…" : sharedId ? "Copy share link" : "Save & copy share link"}
          </button>
          {shareStatus === "error" ? <p className="share-error">The map could not be saved. Please try again.</p> : null}
          {shareNotice ? <p className="share-notice">{shareNotice}</p> : null}
          {shareUrl ? <p className="share-success">Share link: <a href={shareUrl}>{shareUrl}</a></p> : null}
          {socialLinks ? (
            <div className="social-share" aria-labelledby="social-share-title">
              <p id="social-share-title">Share this map</p>
              <div className="social-share-grid">
                <SocialShareLink label="Twitter" href={socialLinks.twitter} icon={FaTwitter} />
                <SocialShareLink label="Bluesky" href={socialLinks.bluesky} icon={FaBluesky} />
                <SocialShareLink label="SMS" href={socialLinks.sms} icon={FaSms} external={false} />
                <SocialShareLink label="Reddit" href={socialLinks.reddit} icon={FaRedditAlien} />
                <SocialShareLink label="WhatsApp" href={socialLinks.whatsapp} icon={FaWhatsapp} />
                <SocialShareLink label="Facebook" href={socialLinks.facebook} icon={FaFacebookF} />
              </div>
            </div>
          ) : null}
        </div>
        <p className="certificate-foot">Based only on this map · Change it whenever you want</p>
      </section>
    </div>
  );
}

interface SocialShareLinkProps {
  label: string;
  href: string;
  icon: IconType;
  external?: boolean;
}

function SocialShareLink({ label, href, icon: Icon, external = true }: SocialShareLinkProps) {
  return (
    <a
      className="social-share-link"
      href={href}
      aria-label={`Share via ${label}`}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
    >
      <Icon aria-hidden="true" focusable="false" />
      <span>{label}</span>
    </a>
  );
}

function firstUnrated(ratings: Ratings): string | null {
  const feature = orderedNeighborhoods.find((item) => ratings[String(item.properties.id)] === undefined);
  return feature ? String(feature.properties.id) : String(orderedNeighborhoods[0].properties.id);
}

function nextUnrated(currentId: string | null, ratings: Ratings): string | null {
  const startIndex = Math.max(0, orderedNeighborhoods.findIndex((item) => String(item.properties.id) === currentId));
  for (let offset = 1; offset <= orderedNeighborhoods.length; offset += 1) {
    const feature = orderedNeighborhoods[(startIndex + offset) % orderedNeighborhoods.length];
    if (ratings[String(feature.properties.id)] === undefined) return String(feature.properties.id);
  }
  return currentId;
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textArea = document.createElement("textarea");
  textArea.value = value;
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.append(textArea);
  textArea.select();
  document.execCommand("copy");
  textArea.remove();
}

function isSharedMap(value: unknown): value is SharedMap {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.id === "string"
    && typeof candidate.title === "string"
    && typeof candidate.createdAt === "number"
    && !!candidate.ratings
    && typeof candidate.ratings === "object"
    && !Array.isArray(candidate.ratings);
}

function isSaveResponse(value: unknown): value is { id: string; path: string } {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.id === "string" && typeof candidate.path === "string";
}

function isEditableTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement
    || target instanceof HTMLTextAreaElement
    || target instanceof HTMLSelectElement
    || (target instanceof HTMLElement && target.isContentEditable);
}

function isFocusableElement(element: Element | null): element is FocusableElement {
  return !!element && "focus" in element && typeof element.focus === "function";
}

function restoreFocusAfterRender(element: FocusableElement): void {
  requestAnimationFrame(() => {
    if (element.isConnected) element.focus();
    else document.querySelector<HTMLElement>("#neighborhood-search")?.focus();
  });
}

function leaveSharedRoute(): void {
  if (/^\/m\/[A-Za-z0-9_-]{12}$/.test(window.location.pathname)) {
    window.history.replaceState({}, "", "/");
  }
}
